import { useEffect, type ReactNode } from "react";
import { Icon } from "./Icon";

export function StatusBadge({ passed }: { passed: boolean }) {
  return (
    <span className={`badge ${passed ? "ok" : "bad"}`}>
      <span className="dot" />
      {passed ? "Prêt à imprimer" : "Non validé"}
    </span>
  );
}

export function Badge({ children, kind }: { children: ReactNode; kind?: "ok" | "bad" | "warn" | "accent" }) {
  return <span className={`badge ${kind ?? ""}`}>{children}</span>;
}

export function Spinner() {
  return <span className="spinner" />;
}

export function Empty({ icon = "box", children }: { icon?: string; children: ReactNode }) {
  return (
    <div className="empty blueprint">
      <div className="big">
        <Icon name={icon} size={44} />
      </div>
      {children}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // Standard popup behaviour: Escape closes, and the page behind doesn't scroll.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row between modal-head">
          <h2>{title}</h2>
          <button className="btn ghost icon sm" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="row between modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// Small inline sparkline (decorative trend accent on stat cards).
export function Spark({ points, color = "var(--accent-3)", w = 72, h = 24 }: { points: number[]; color?: string; w?: number; h?: number }) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - ((p - min) / span) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </svg>
  );
}

// Circular printability score gauge — colour follows the INSPECTOR verdict bands.
export function ScoreGauge({ value, size = 92, stroke = 9, label }: { value: number; size?: number; stroke?: number; label?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value));
  const color = pct >= 0.85 ? "var(--ok)" : pct >= 0.7 ? "var(--warn)" : "var(--bad)";
  return (
    <div className="gauge" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle className="gauge-track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="gauge-val" style={{ fontSize: size * 0.26, color }}>
        {Math.round(pct * 100)}
        <span style={{ fontSize: size * 0.13, color: "var(--muted)" }}>%</span>
        {label && (
          <div style={{ fontSize: 10, color: "var(--muted)", textAlign: "center", fontWeight: 400, letterSpacing: "0.08em" }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
}
